import {
  FC,
  BaseSyntheticEvent,
  FormEventHandler,
  memo,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  LoginFlow,
  RegistrationFlow,
  SettingsFlow,
  VerificationFlow,
} from '@ory/client';
import { useLocation } from 'react-router';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Error,
  Image,
  Input,
  Message,
  Radio,
  SelectInput,
  Switch,
  Tabs,
  Text,
  WebauthScript,
  GroupButtons,
  VerificationEmail,
} from 'components';

import PhoneInput, { Value as PhoneNumberType } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import 'react-phone-number-input/style.css';

import {
  ButtonStyle,
  ButtonType,
  Client,
  DividerText,
  DividerType,
  FlowStrategy,
  IconType,
  InputType,
  MessageType,
  NodeType,
} from 'enum';
import {
  getCountries,
  getErrors,
  getMessages,
  updateMessageText,
  removeEmpty,
  removeObjectValueByKeys,
  resetRadioInputs,
  validateEmailMatch,
  validatePasswordMatch,
  stripVerificationFields,
} from 'helpers';
import { Message as MessageText, Maps, ConstantsTest, CitizenType } from '../../constants/';
import { BaseFlow, ErrorGlobal, NodeFlow } from 'interfaces';
import { routes } from '../../models/routes';
import { capitalizeFirstLetters } from 'utils';
import { UsernamePattern, UsernamePatternErrorMessage } from './constants';

interface FlowProps {
  flow:
  | LoginFlow
  | RegistrationFlow
  | VerificationFlow
  | SettingsFlow
  | BaseFlow;
  error?: ErrorGlobal;
  flowStrategy: FlowStrategy;
  loginChallenge?: string;
  activeStrategy?: FlowStrategy;
  showMessages?: boolean;
  hideFlow?: boolean;
  onChangeTab?: (event: BaseSyntheticEvent) => void;
  onSubmit?: (
    values: { [key: string]: string },
    flowStrategy?: FlowStrategy,
  ) => void;
}

const createOptions = (defaultOption: string, options: string[]) => {
  const selectOptions = options.map((option: string) => {
    return {
      name: option,
      value: option,
    };
  });
  return [
    {
      name: defaultOption,
      value: '',
    },
    ...selectOptions,
  ];
};

// Constants
const NRC_PATTERN = /^[0-9]{9}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NRC_REGEX = '^[0-9]{9}$';

// Helper functions
const validateNRC = (value: string): string => value && !NRC_PATTERN.test(value) ? MessageText.USERNAME_CITIZEN_VALIDATION_ERROR : '';
const validateEmail = (value: string): string => value && !EMAIL_PATTERN.test(value) ? MessageText.EMAIL_VALIDATION_ERROR : '';
const isCitizen = (personType: string): boolean => personType === CitizenType.ZambianCitizen;

const validateRequiredFields = (values: { [key: string]: string }, flowStrategy: FlowStrategy): { [key: string]: string } => {
  if (flowStrategy !== FlowStrategy.SignUp) return {};

  const requiredFields: Array<{ key: string; message: string }> = [
    { key: Client.PersonType, message: MessageText.PERSON_TYPE_REQUIRED },
    { key: Client.FirstName, message: MessageText.FIRST_NAME_REQUIRED },
    { key: Client.LastName, message: MessageText.LAST_NAME_REQUIRED },
    { key: Client.Traits_Email, message: MessageText.EMAIL_REQUIRED },
    { key: Client.EmailVerification, message: MessageText.EMAIL_VERIFICATION_REQUIRED },
    { key: Client.Password, message: MessageText.PASSWORD_REQUIRED },
    { key: Client.PasswordVerification, message: MessageText.PASSWORD_VERIFICATION_REQUIRED },
    { key: Client.Username, message: MessageText.USERNAME_REQUIRED },
  ];

  const errors: { [key: string]: string } = {};
  for (const { key, message } of requiredFields) {
    if (!values[key] || !values[key].trim()) {
      errors[key] = message;
    }
  }
  return errors;
};

export const Flow: FC<FlowProps> = memo(
  ({
    flow,
    flowStrategy,
    activeStrategy,
    error,
    loginChallenge,
    showMessages = true,
    hideFlow,
    onChangeTab,
    onSubmit,
  }: FlowProps) => {
    const [formState, setFormState] = useState<{
      values: { [key: string]: string };
    }>({
      values: {},
    });

    const [personType, setPersonType] = useState<string>(CitizenType.ZambianCitizen);
    const [usernameValidationError, setUsernameValidationError] = useState<string>('');
    const [emailValidationError, setEmailValidationError] = useState<string>('');
    const [emailVerificationError, setEmailVerificationError] = useState<string>('');
    const [passwordVerificationError, setPasswordVerificationError] = useState<string>('');
    const [usernameServerError, setUsernameServerError] = useState<string>('');
    const [emailServerError, setEmailServerError] = useState<string>('');
    const [passwordServerError, setPasswordServerError] = useState<string>('');
    const [requiredFieldErrors, setRequiredFieldErrors] = useState<{ [key: string]: string }>({});

    const location = useLocation();

    const message = useMemo(() => {
      return updateMessageText(getMessages(flow?.ui?.messages || []));
    }, [flow]);

    const onFilterValues = (
      values: { [key: string]: string },
      keyValue: string,
    ) => {
      const found =
        Client.LookupSecretDisable in values &&
        (Client.LookupSecretReveal in values ||
          Client.LookupSecretRegenerate in values);
      if (found) {
        Object.keys(values).forEach((key: string) => {
          if (key !== Client.CsrfToken && key !== keyValue) {
            delete values[key];
          }
        });
      }
      return values;
    };

    const onSubmitHandler = (
      e: BaseSyntheticEvent & { nativeEvent: { submitter: { name: string } } },
    ) => {
      let { values } = formState;

      // Prevent submission if verification fields don't match
      if (flowStrategy === FlowStrategy.SignUp) {
        // Ensure personType value is synchronized with form values for validation
        if (personType) {
          values[Client.PersonType] = personType;
        }

        // Required field validation
        const reqErrors = validateRequiredFields(values, flowStrategy);
        setRequiredFieldErrors(reqErrors);
        if (Object.keys(reqErrors).length > 0) {
          const firstErrorKey = Object.keys(reqErrors)[0];
          if (firstErrorKey) {
            document.querySelector(`[name="${firstErrorKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (document.querySelector(`[name="${firstErrorKey}"]`) as HTMLElement)?.focus();
          }
          e.stopPropagation();
          e.preventDefault();
          return;
        }

        if (emailVerificationError || passwordVerificationError) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        // Strip verification fields before submitting to backend
        values = stripVerificationFields(values);
      }

      if (
        !loginChallenge &&
        ![
          FlowStrategy.SignIn,
          FlowStrategy.Recovery,
          FlowStrategy.SignUp,
          FlowStrategy.FirstVerification,
        ].includes(flowStrategy)
      ) {
        if (flowStrategy === FlowStrategy.LookupSecret) {
          values = onFilterValues(values, e?.nativeEvent?.submitter?.name);
        }

        if (
          flowStrategy === FlowStrategy.Totp &&
          location.pathname === routes.accessSettings
        ) {
          values = { ...values, [Client.Authenticator]: true + '' };
        }

        if (
          flowStrategy === FlowStrategy.Password &&
          location.pathname === routes.accessSettings &&
          e?.nativeEvent?.submitter?.name === Client.Method
        ) {
          values = removeObjectValueByKeys(values, [Client.PasswordRemove]);
        }

        if (e?.nativeEvent?.submitter?.name === Client.ResetOtp) {
          values = { ...values, [Client.SendOtp]: 'true' };
          values[Client.OtpCode] && delete values[Client.OtpCode];
        }

        values = { ...removeEmpty(values) };
        onSubmit?.(values, flowStrategy);
        if (flowStrategy != FlowStrategy.SignInQRCode) {
          e.stopPropagation();
          e.preventDefault();
        }
      }
    };

    const onChangeHandler = (e: BaseSyntheticEvent, inputType: InputType) => {
      setRequiredFieldErrors((prev) => {
        if (prev[e.target.name]) {
          const { [e.target.name]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });

      setFormState((currentState) => {
        const values = Object.assign(
          { ...currentState.values },
          inputType === InputType.RADIO && resetRadioInputs(flow.ui?.nodes),
          {
            [e.target.name]:
              // PersonType radio buttons should store the value, not checked status
              e.target.name === Client.PersonType
                ? e.target.value
                : [InputType.RADIO, InputType.CHECKBOX].includes(inputType)
                  ? +e.target.checked
                  : e.target.value,
          },
        );

        // Track personType changes for dynamic username label
        if (e.target.name === Client.PersonType) {
          setPersonType(e.target.value);
          // Re-validate username when person type changes
          const currentUsername = values[Client.Username];
          if (currentUsername && flowStrategy === FlowStrategy.SignUp) {
            setUsernameValidationError(
              isCitizen(e.target.value) ? validateNRC(currentUsername) : ''
            );
          }
        }

        // Validate username based on person type
        if (e.target.name === Client.Username && flowStrategy === FlowStrategy.SignUp) {
          setUsernameValidationError(
            isCitizen(personType) ? validateNRC(e.target.value) : ''
          );
          // Clear username server error when user modifies the field
          setUsernameServerError('');
        }

        // Clear username server error on sign-in when field is modified
        if (e.target.name === Client.Identifier && flowStrategy === FlowStrategy.SignIn) {
          setUsernameServerError('');
        }

        // Validate email verification
        if (e.target.name === Client.Traits_Email || e.target.name === Client.EmailVerification) {
          const email = e.target.name === Client.Traits_Email ? e.target.value : values[Client.Traits_Email];
          const emailVerification = e.target.name === Client.EmailVerification ? e.target.value : values[Client.EmailVerification];

          // Validate email format
          if (e.target.name === Client.Traits_Email && flowStrategy === FlowStrategy.SignUp) {
            setEmailValidationError(validateEmail(e.target.value));
            // Clear email server error when user modifies the field
            setEmailServerError('');
          }

          setEmailVerificationError(validateEmailMatch(email, emailVerification));
        }

        // Validate password verification
        if (e.target.name === Client.Password || e.target.name === Client.PasswordVerification) {
          const password = e.target.name === Client.Password ? e.target.value : values[Client.Password];
          const passwordVerification = e.target.name === Client.PasswordVerification ? e.target.value : values[Client.PasswordVerification];
          setPasswordVerificationError(validatePasswordMatch(password, passwordVerification));
          // Clear password server error when user modifies the field
          if (e.target.name === Client.Password) {
            setPasswordServerError('');
          }
        }

        if (e.target.name.startsWith('transient')) {
          const fieldName = e.target.name.substring(10);
          values[
            'transient_payload'
          ] = `{"${fieldName}" : "${e.target.value}"}`;
        }

        if (
          (inputType === InputType.CHECKBOX &&
            flowStrategy === FlowStrategy.Mfa) ||
          (location.pathname === routes.notifications &&
            e.target.name === Client.Enable)
        ) {
          onSubmit?.(values, flowStrategy);
        }

        return { ...currentState, values };
      });
    };

    const onChangeOtpCode = useCallback((otpCode: string) => {
      setFormState((currentState) => ({
        ...currentState,
        values: {
          ...currentState.values,
          [Client.OtpCode]: otpCode,
        },
      }));
    }, []);

    const setPhoneNumber = (value: string, name: string) => {
      setFormState((currentState) => {
        return {
          ...currentState,
          values: {
            ...currentState.values,
            [name]: value,
          },
        };
      });
    };

    const onClickWebauth = (e: BaseSyntheticEvent, onclick: string) => {
      if (e.target.name == Client.WebAuthRegisterTrigger) {
        const keyNameInput = document.getElementById(
          Client.WebAuthRegisterDisplayName,
        ) as HTMLInputElement;
        if (keyNameInput?.value == '') {
          alert('Please fill out the name of the security key');
          keyNameInput.focus();
          return;
        }
      }

      e.stopPropagation();
      e.preventDefault();
      const run = new Function(onclick);
      run();
      return;
    };

    const getButtonStyleByGroup = (
      name: string,
      group: string,
    ) => {
      const outlineButtonsMap = [
        'method_webauthn',
        'passkey_login_trigger_passkey',
      ];
      return outlineButtonsMap.includes(`${name}_${group}`)
        ? 'btn btn-outline-primary'
        : 'btn btn-primary';
    };

    const getButtonStyleByStrategy = useMemo(() => {
      return [
        FlowStrategy.SignInSecurityKey,
        FlowStrategy.SignUpSecurityKey,
      ].includes(flowStrategy)
        ? ButtonStyle.OUTLINEPRIMARY
        : ButtonStyle.PRIMARY;
    }, [flowStrategy]);

    const getIconNameByStrategy = (
      strategy: FlowStrategy,
      iconName: IconType,
    ) => {
      return [
        FlowStrategy.SignInSecurityKey,
        FlowStrategy.SignUpSecurityKey,
      ].includes(strategy)
        ? Maps.INPUT_ICON_MAP.get(IconType.Key)
        : Maps.INPUT_ICON_MAP.get(iconName);
    };

    const getTooltipByStrategy = (
      strategy: FlowStrategy,
      inputName: string,
    ) => {
      return (
        ([
          FlowStrategy.SignInSecurityKey,
          FlowStrategy.SignUpSecurityKey,
          FlowStrategy.SignUp,
          FlowStrategy.SignIn,
        ].includes(strategy) &&
          (inputName === IconType.Identifier ||
            inputName === IconType.NationalId ||
            inputName === IconType.WebAuthDisplayName)) ||
        (FlowStrategy.SignUp === strategy && inputName === IconType.Password)
      );
    };

    const getWrapperClassesByStrategy = useMemo(() => {
      return flowStrategy === FlowStrategy.Settings
        ? 'd-inline-block col-12 col-lg-6 py-1 px-2'
        : '';
    }, [flowStrategy]);

    const renderPersonTypeRadio = useCallback((name: string, text: string, required: boolean) => {
      const radioOption = (id: string, value: string, label: string) => (
        <>
          <input
            type="radio"
            className="btn-check"
            name={name}
            id={id}
            value={value}
            checked={personType === value}
            onChange={(e) => onChangeHandler(e, InputType.RADIO)}
            autoComplete="off"
          />
          <label
            className={`btn btn-outline-primary ${personType === value ? 'active' : ''}`}
            htmlFor={id}
            style={{
              borderWidth: '2px',
              fontWeight: personType === value ? '600' : '500'
            }}
          >
            {label}
          </label>
        </>
      );

      return (
        <div key={name} className={`${getWrapperClassesByStrategy} mb-3`}>
          <label className="form-label mb-2">
            {text} {required && <span className="text-danger">*</span>}
          </label>
          <div className="btn-group w-100" role="group" aria-label="Person type selection">
            {radioOption('personType-citizen', CitizenType.ZambianCitizen, CitizenType.ZambianCitizen)}
            {radioOption('personType-foreigner', CitizenType.ForeignCitizen, CitizenType.ForeignCitizen)}
          </div>
          {requiredFieldErrors[name] && <Error message={requiredFieldErrors[name]} />}
        </div>
      );
    }, [personType, getWrapperClassesByStrategy, requiredFieldErrors]);

    const renderFlow = useCallback(
      (
        flowNodes:
          | LoginFlow
          | RegistrationFlow
          | VerificationFlow
          | SettingsFlow
          | BaseFlow,
      ) => {
        const { values } = formState;
        return flowNodes?.ui?.nodes.map((node: Record<string, any>) => {
          const {
            disabled,
            name,
            required,
            type,
            options,
            value,
            title,
            href,
            crossorigin,
            id,
            classname,
            iconclassname,
            iconstyle,
            integrity,
            nonce,
            referrerpolicy,
            src,
            width,
            height,
            hidden,
            pattern,
            onclick,
          } = node.attributes;
          const { tabs } = node;
          const secret = node.attributes?.text?.text;
          const { text } = node.meta.label || { text: name };

          const isSignUpField = flowStrategy === FlowStrategy.SignUp;
          const isSpecifiedRequired = isSignUpField && [
            Client.PersonType,
            Client.FirstName,
            Client.LastName,
            Client.Traits_Email,
            Client.EmailVerification,
            Client.Password,
            Client.PasswordVerification,
            Client.Username,
          ].includes(name as Client);

          const fieldRequired = required || isSpecifiedRequired;
          const fieldError = requiredFieldErrors[name] || getErrors(node.messages);
          if (node.attributes.type === InputType.DATE) {
            return (
              <DatePicker
                error={getErrors(node.messages)}
                wrapperClassname={getWrapperClassesByStrategy}
                onChange={(e) => onChangeHandler(e, type)}
                value={values ? values[name] : ''}
                className="form-control"
                label={text}
                key={name}
                disabled={disabled}
                name={name}
                type={type}
                required={fieldRequired}
              />
            );
          } else if (node.attributes.type === InputType.TELEPHONE) {
            const errorMessage = getErrors(node.messages);
            return (
              <div
                key={name}
                className={`mb-2 ${node.attributes.hidden ? 'd-none' : ''}`}
              >
                <div className={`focus-label ${errorMessage ? 'error' : ''}`}>
                  <label
                    htmlFor={name}
                    className={`form-check-label form-label ${required ? 'required' : ''
                      }`}
                  >
                    {text}
                  </label>
                  <PhoneInput
                    international
                    flags={flags}
                    key={name}
                    name={name}
                    required={fieldRequired}
                    disabled={hidden || disabled}
                    className="d-flex form-control"
                    value={values[name]}
                    onChange={(phoneValue) =>
                      setPhoneNumber(phoneValue as PhoneNumberType, name)
                    }
                  />
                  {(fieldError || errorMessage) && <Error message={(fieldError || errorMessage) || ''} />}
                </div>
              </div>
            );
          } else if (
            node.attributes.type === InputType.CHECKBOX &&
            !location.pathname.includes(routes.settings)
          ) {
            return (
              <Checkbox
                onChange={(e) => onChangeHandler(e, type)}
                type={type}
                key={name}
                name={name}
                label={text}
                value={values ? values[name] : ''}
                checked={!!values[name]}
                hidden={hidden}
              />
            );
          } else if (
            node.attributes.type === InputType.RADIO &&
            !location.pathname.includes(routes.settings)
          ) {
            return (
              <Radio
                onChange={(e) => onChangeHandler(e, type)}
                type={type}
                key={name}
                name={name}
                label={text}
                value={values[name]}
                checked={!!values[name]}
              />
            );
          } else if (
            node.attributes.type === InputType.CHECKBOX &&
            location.pathname.includes(routes.settings)
          ) {
            return (
              <Switch
                cardClass={`${![
                  FlowStrategy.NotificationPreferencesEmail,
                  FlowStrategy.NotificationPreferencesPush,
                  FlowStrategy.NotificationPreferencesSms,
                ].includes(flowStrategy)
                  ? ''
                  : 'px-0'
                  }`}
                onChange={(e) => onChangeHandler(e, type)}
                error={getErrors(node.messages)}
                type={type}
                key={name}
                withDivider={name === Client.Authenticator}
                tooltip={name === Client.Authenticator}
                tooltipMessage={MessageText.TOOLTIP_MFA_MESSAGE}
                name={name}
                disabled={disabled}
                label={text}
                labelClass={`${![
                  FlowStrategy.NotificationPreferencesEmail,
                  FlowStrategy.NotificationPreferencesPush,
                  FlowStrategy.NotificationPreferencesSms,
                ].includes(flowStrategy)
                  ? 'ms-2'
                  : ''
                  }`}
                value={values ? values[name] : ''}
                checked={values[name] ? !!values[name] : node.attributes.value}
                className={
                  [
                    FlowStrategy.NotificationPreferencesEmail,
                    FlowStrategy.NotificationPreferencesPush,
                    FlowStrategy.NotificationPreferencesSms,
                  ].includes(flowStrategy)
                    ? 'float-end'
                    : ''
                }
              />
            );
          } else if (node.attributes.type === InputType.SCRIPT) {
            return (
              <WebauthScript
                key={id}
                type={type}
                async={false}
                crossorigin={crossorigin}
                id={id}
                integrity={integrity}
                nonce={nonce}
                referrerpolicy={referrerpolicy}
                src={src}
              />
            );
          } else if (node.type === NodeType.A) {
            return (
              <Button
                key={title.text}
                onClick={() => window.location.replace(href)}
                name={name}
                className={`${ButtonStyle.PRIMARY} my-3 w-100`}
                type={ButtonType.BUTTON}
              >
                {capitalizeFirstLetters(title.text)}
              </Button>
            );
          } else if (text === 'Country') {
            const selectOptions = createOptions(text, getCountries());
            return (
              <SelectInput
                wrapperClassname={getWrapperClassesByStrategy}
                error={fieldError}
                onChange={(e) => onChangeHandler(e, type)}
                selectedValue={values ? values[name] : ''}
                key={name}
                name={name}
                label={text}
                type={InputType.SELECT}
                disabled={disabled}
                required={fieldRequired}
                options={selectOptions}
              />
            );
          } else if (
            node.attributes.options &&
            node.attributes.options.length
          ) {
            const selectOptions = createOptions(text, options);
            return (
              <SelectInput
                wrapperClassname={getWrapperClassesByStrategy}
                error={fieldError}
                onChange={(e) => onChangeHandler(e, type)}
                selectedValue={values ? values[name] : ''}
                key={name}
                name={name}
                label={text}
                type={InputType.SELECT}
                disabled={disabled}
                required={fieldRequired}
                options={selectOptions}
              />
            );
          } else if (node.attributes.type === InputType.BUTTON) {
            return (
              <Button
                key={name}
                onClick={(e) => onClickWebauth(e, onclick)}
                name={name}
                className={`${getButtonStyleByGroup(name, node.group)} ${location.pathname.includes(routes.settings)
                  ? 'float-end mt-3'
                  : 'my-3 w-100'
                  }`}
                type={type}
              >
                {capitalizeFirstLetters(text)}
              </Button>
            );
          } else if (
            node.attributes.type === InputType.IMG ||
            node.type === InputType.IMG
          ) {
            return (
              <Image
                key={id}
                id={id}
                src={src}
                width={width}
                height={height}
                text={text}
                hidden={hidden}
              />
            );
          } else if (node.type === InputType.TEXT) {
            return (
              <Text
                key={id}
                name={name}
                id={id}
                text={text}
                code={secret}
                onChange={(e) => onChangeHandler(e, type)}
                flowStrategy={flowStrategy}
                label={name}
                hidden={hidden}
              />
            );
          } else if (node.type === InputType.DIVIDER) {
            return (
              <Divider
                key={name}
                type={DividerType.Text}
                text={
                  flowStrategy === FlowStrategy.SignIn
                    ? DividerText.Or
                    : DividerText.And
                }
                className="my-3"
              />
            );
          } else if (node.type === InputType.TABS) {
            // Hide tabs for both sign-up and sign-in flows
            return null;
          } else if (node.attributes.type === InputType.SUBMIT) {
            return (
              <Button
                wrapperClassname={
                  flowStrategy === FlowStrategy.Settings ? 'col-12 px-2' : ''
                }
                key={`${node.group}-${name}`}
                errorText={getErrors(node.messages)}
                className={
                  classname ??
                  `${getButtonStyleByGroup(name, node.group)} ${location.pathname.includes(routes.settings)
                    ? 'float-end mt-3'
                    : 'mt-3 mb-3 w-100'
                  }`
                }
                type={type}
                name={name}
                hidden={!!hidden}
                value={value}
                disabled={disabled}
                formNoValidate={[Client.Username, Client.Identifier].includes(
                  node.attributes.name,
                )}
              >
                <i className={iconclassname} style={iconstyle} />
                {capitalizeFirstLetters(text)}
              </Button>
            );
          } else if (node.attributes.type === InputType.GROUPBUTTONS) {
            return (
              <GroupButtons
                wrapperClassname={node.wrapperClassname ?? ''}
                key={name}
                buttons={node.buttons}
              />
            );
          } else if (node.attributes.type === InputType.VERIFICATIONEMAIL) {
            return (
              <VerificationEmail
                name={name}
                key={name}
                onChange={onChangeOtpCode}
                isSms={value}
              />
            );
          } else {
            // Handle personType as radio buttons for registration
            if (name === Client.PersonType && flowStrategy === FlowStrategy.SignUp) {
              return renderPersonTypeRadio(name, text, required);
            }

            // Dynamic username label and guidance based on personType
            const isUsernameSignUp = name === Client.Username && flowStrategy === FlowStrategy.SignUp;
            const isUsernameSignIn = (name === Client.Username || name === Client.Identifier) && flowStrategy === FlowStrategy.SignIn;
            const citizenConfig = {
              label: MessageText.USERNAME_CITIZEN_LABEL,
              guidance: MessageText.USERNAME_CITIZEN_GUIDANCE,
            };
            const foreignerConfig = {
              label: MessageText.USERNAME_FOREIGNER_LABEL,
              guidance: MessageText.USERNAME_FOREIGNER_GUIDANCE,
            };
            const usernameConfig = isCitizen(personType) ? citizenConfig : foreignerConfig;

            const dynamicLabel = isUsernameSignUp ? usernameConfig.label : text;
            const dynamicGuidance = isUsernameSignUp ? usernameConfig.guidance : Maps.TOOLTIP_MESSAGE_MAP.get(name);
            const dynamicHelperText = isUsernameSignUp
              ? usernameConfig.guidance
              : isUsernameSignIn
                ? MessageText.USERNAME_SIGNIN_GUIDANCE
                : undefined;

            // Handle email verification field for registration
            if (name === Client.Traits_Email && flowStrategy === FlowStrategy.SignUp) {
              return [
                <Input
                  wrapperClassname={`${getWrapperClassesByStrategy} mb-2`}
                  error={requiredFieldErrors[name] || emailValidationError || emailServerError}
                  onChange={(e) => onChangeHandler(e, type)}
                  value={values ? values[name] : ''}
                  className="form-control"
                  key={name}
                  name={name}
                  type={type}
                  label={text}
                  autoCapitalize="none"
                  disabled={disabled}
                  required={fieldRequired}
                  icon={!!Maps.INPUT_ICON_MAP.get(name)}
                  iconName={getIconNameByStrategy(flowStrategy, name)}
                  tooltip={getTooltipByStrategy(flowStrategy, name)}
                  tooltipMessage={dynamicGuidance}
                />,
                <Input
                  wrapperClassname={`${getWrapperClassesByStrategy} mb-2`}
                  error={requiredFieldErrors[Client.EmailVerification] || emailVerificationError}
                  onChange={(e) => onChangeHandler(e, type)}
                  value={values ? values[Client.EmailVerification] : ''}
                  className="form-control"
                  key={Client.EmailVerification}
                  name={Client.EmailVerification}
                  type={type}
                  label="Re-type Email Address"
                  autoCapitalize="none"
                  disabled={disabled}
                  required={isSignUpField}
                />
              ];
            }

            // Handle password verification field for registration
            if (name === Client.Password && flowStrategy === FlowStrategy.SignUp) {
              return [
                <Input
                  wrapperClassname={`${getWrapperClassesByStrategy} mb-2`}
                  error={requiredFieldErrors[name] || passwordServerError}
                  onChange={(e) => onChangeHandler(e, type)}
                  value={values ? values[name] : ''}
                  className="form-control"
                  key={name}
                  name={name}
                  type={type}
                  label={text}
                  pattern={
                    flowStrategy !== FlowStrategy.SignUp
                      ? undefined
                      : pattern
                        ? pattern
                        : name == Client.Identifier
                          ? UsernamePattern
                          : undefined
                  }
                  title={
                    title
                      ? title
                      : name == Client.Username || name == Client.Identifier
                        ? UsernamePatternErrorMessage
                        : undefined
                  }
                  autoCapitalize="none"
                  disabled={disabled}
                  required={fieldRequired}
                  icon={!!Maps.INPUT_ICON_MAP.get(name)}
                  iconName={getIconNameByStrategy(flowStrategy, name)}
                  tooltip={getTooltipByStrategy(flowStrategy, name)}
                  tooltipMessage={dynamicGuidance}
                />,
                <Input
                  wrapperClassname={`${getWrapperClassesByStrategy} mb-2`}
                  error={requiredFieldErrors[Client.PasswordVerification] || passwordVerificationError}
                  onChange={(e) => onChangeHandler(e, type)}
                  value={values ? values[Client.PasswordVerification] : ''}
                  className="form-control"
                  key={Client.PasswordVerification}
                  name={Client.PasswordVerification}
                  type={type}
                  label="Re-type Password"
                  autoCapitalize="none"
                  disabled={disabled}
                  required={isSignUpField}
                />
              ];
            }

            // Dynamic pattern and validation message for username based on person type
            const getUsernameValidation = () => {
              if (name === Client.Username && flowStrategy === FlowStrategy.SignUp) {
                return isCitizen(personType)
                  ? { pattern: NRC_REGEX, title: MessageText.USERNAME_CITIZEN_VALIDATION_ERROR }
                  : { pattern: pattern || UsernamePattern, title: UsernamePatternErrorMessage };
              }
              if (name === Client.Identifier) {
                return { pattern: UsernamePattern, title: UsernamePatternErrorMessage };
              }
              return { pattern, title };
            };

            const { pattern: dynamicPattern, title: dynamicTitle } = getUsernameValidation();
            const dynamicError = requiredFieldErrors[name]
              ? requiredFieldErrors[name]
              : (name === Client.Username && usernameValidationError)
                ? usernameValidationError
                : (name === Client.Username || name === Client.Identifier) && usernameServerError
                  ? usernameServerError
                  : (name === Client.Username || name === Client.Identifier) ? '' : fieldError;

            return (
              <Input
                wrapperClassname={`${getWrapperClassesByStrategy} mb-2`}
                error={dynamicError}
                onChange={(e) => onChangeHandler(e, type)}
                value={values ? values[name] : ''}
                className="form-control"
                key={name}
                name={name}
                type={type}
                label={dynamicLabel}
                pattern={
                  flowStrategy === FlowStrategy.SignIn
                    ? undefined
                    : dynamicPattern
                }
                title={dynamicTitle}
                autoCapitalize="none"
                disabled={disabled}
                required={fieldRequired}
                icon={!!Maps.INPUT_ICON_MAP.get(name)}
                iconName={getIconNameByStrategy(flowStrategy, name)}
                tooltip={getTooltipByStrategy(flowStrategy, name)}
                tooltipMessage={dynamicGuidance}
                helperText={dynamicHelperText}
              />
            );
          }
        });
      },
      [flow, formState, requiredFieldErrors],
    );

    useEffect(() => {
      const values: { [key: string]: string } = {};
      flow?.ui?.nodes.forEach((node: any) => {
        if (
          node.attributes &&
          ![InputType.BUTTON, InputType.DIVIDER, InputType.TABS].includes(
            node.attributes.type as InputType,
          )
        ) {
          values[node.attributes.name as string] =
            formState.values[node.attributes.name as string] &&
              !location.pathname.includes(routes.settings)
              ? formState.values[node.attributes.name as string]
              : (node.attributes.value as string);
        }
        if (
          [
            FlowStrategy.NotificationPreferencesEmail,
            FlowStrategy.NotificationPreferencesSms,
          ].includes(node.group as FlowStrategy) &&
          node?.attributes?.type === InputType.CHECKBOX &&
          location.pathname.includes(routes.settings)
        ) {
          values[node.attributes.name as string] =
            formState.values[node.attributes.name as string];
        }

        // Capture server validation errors for username, email, and password
        if (node.attributes?.name === Client.Username || node.attributes?.name === Client.Identifier) {
          const errorMessage = getErrors(node.messages || []);
          if (errorMessage) {
            setUsernameServerError(errorMessage);
          }
        }
        if (node.attributes?.name === Client.Traits_Email) {
          const errorMessage = getErrors(node.messages || []);
          if (errorMessage) {
            setEmailServerError(errorMessage);
          }
        }
        if (node.attributes?.name === Client.Password) {
          const errorMessage = getErrors(node.messages || []);
          if (errorMessage) {
            setPasswordServerError(errorMessage);
          }
        }
      });

      // Sync personType state with backend value when flow changes
      if (values[Client.PersonType] && flowStrategy === FlowStrategy.SignUp) {
        setPersonType(values[Client.PersonType]);
      }

      setFormState((currentState) => ({
        ...currentState,
        values: values,
      }));
    }, [flow]);

    return (
      <form
        onSubmit={onSubmitHandler as FormEventHandler}
        action={flow?.ui?.action}
        method={flow?.ui?.method}
        hidden={!!hideFlow}
        data-testid={ConstantsTest.FORM}
        noValidate
      >
        {error?.message && <Error message={error.message} />}
        {showMessages && message && flowStrategy === activeStrategy && (
          <div className="py-3">
            <Message type={MessageType.Text} message={message} />
          </div>
        )}
        {flow.ui && renderFlow(flow)}
      </form>
    );
  },
);
